import React, { createContext, useState, useEffect } from 'react';
import api, { APIError } from '../services/api';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Enhanced error handling
  const handleAuthError = (error, context = '') => {
    // Auth error occurred
    
    let errorMessage = 'An authentication error occurred';
    
    if (error instanceof APIError) {
      errorMessage = error.message;
      
      // Handle specific auth errors
      if (error.code === 'UNAUTHORIZED' || error.status === 401) {
        // Clear invalid auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setCurrentUser(null);
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'TIMEOUT_ERROR') {
        errorMessage = 'Request timed out. Please try again.';
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    return errorMessage;
  };

  // Clear error state
  const clearError = () => {
    setError(null);
  };

  // Validate token format
  const isValidToken = (token) => {
    if (!token || typeof token !== 'string') return false;
    
    // Basic JWT format check (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    try {
      // Check if payload can be parsed
      const payload = JSON.parse(atob(parts[1]));
      
      // Check if token is expired
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  };

  // Validate user data
  const isValidUser = (user) => {
    return user && 
           typeof user === 'object' && 
           (user.id || user.ID) && 
           (user.email || user.Email);
    // Username is optional since it might be empty
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        clearError();
        
        const token = localStorage.getItem('token');
        const userDataString = localStorage.getItem('user');
        
        if (!token || !userDataString) {
          setLoading(false);
          return;
        }
        
        // Validate token format
        if (!isValidToken(token)) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }
        
        // Parse and validate user data
        let userData;
        try {
          userData = JSON.parse(userDataString);
        } catch (e) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }
        
        if (!isValidUser(userData)) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setLoading(false);
          return;
        }
        
        // Optionally verify token with server
        // This could be a lightweight endpoint like /api/auth/verify
        // For now, we'll trust the stored data if it passes basic validation
        
        setCurrentUser(userData);      } catch (error) {
        handleAuthError(error, 'initialization');
        // Clear potentially corrupted data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setCurrentUser(null);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // The axios interceptor clears stored credentials on any 401, but it cannot
  // reach React state. Without this listener `currentUser` stays populated, so
  // the app keeps rendering authenticated views (comment boxes, edit buttons)
  // whose every request then fails with the same opaque error until a reload.
  useEffect(() => {
    const handleSessionExpired = () => {
      setCurrentUser(null);
      setError('Your session has expired. Please log in again.');
    };

    window.addEventListener('auth:expired', handleSessionExpired);
    return () => window.removeEventListener('auth:expired', handleSessionExpired);
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      clearError();
      
      if (!email || !password) {
        throw new Error('Email and password are required');
      }      const response = await api.post('/api/auth/login', { email, password });
      
      if (!response.data) {
        throw new Error('Invalid response from server');
      }
      
      // Handle standardized response format
      let responseData = response.data;
      if (responseData.success && responseData.data) {
        responseData = responseData.data;
      }
      
      const { token, user } = responseData;
      
      if (!token) {
        throw new Error('No authentication token received');
      }
      
      if (!isValidUser(user)) {
        throw new Error('Invalid user data received from server');
      }
      
      // Validate token format
      if (!isValidToken(token)) {
        throw new Error('Invalid token format received from server');
      }
      
      // Save auth data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setCurrentUser(user);      return user;
    } catch (error) {
      const errorMessage = handleAuthError(error, 'login');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      clearError();
      
      if (!userData || typeof userData !== 'object') {
        throw new Error('Invalid registration data');
      }
      
      if (!userData.email || !userData.password || !userData.username) {
        throw new Error('Email, password, and username are required');
      }      const response = await api.post('/api/auth/register', userData);
      
      if (!response.data) {
        throw new Error('Invalid response from server');
      }
      
      // Handle standardized response format
      let responseData = response.data;
      if (responseData.success && responseData.data) {
        responseData = responseData.data;
      }
      
      const { token, user } = responseData;
      
      if (!token) {
        throw new Error('No authentication token received');
      }
      
      if (!isValidUser(user)) {
        throw new Error('Invalid user data received from server');
      }
      
      // Validate token format
      if (!isValidToken(token)) {
        throw new Error('Invalid token format received from server');
      }
      
      // Save auth data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      setCurrentUser(user);      return user;
    } catch (error) {
      const errorMessage = handleAuthError(error, 'registration');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      clearError();
      
      // Call the server FIRST, while the token is still in localStorage for the
      // request interceptor to attach. /api/auth/logout revokes the presented
      // token server-side, so clearing local storage beforehand would send an
      // unauthenticated request, get a 401, and leave the token valid for its
      // full 7-day lifetime -- exactly the hole revocation exists to close.
      try {
        await api.post('/api/auth/logout');
      } catch (logoutError) {
        // Best effort: if the server is unreachable we still log out locally.
      }

      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setCurrentUser(null);
    } catch (error) {
      // Even if logout fails, we should clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setCurrentUser(null);
      handleAuthError(error, 'logout');
    } finally {
      setLoading(false);
    }
  };

  // Update user data (for profile updates)
  const updateUser = (updatedUserData) => {
    try {
      if (!isValidUser(updatedUserData)) {
        throw new Error('Invalid user data provided');
      }
      
      const newUserData = { ...currentUser, ...updatedUserData };
      localStorage.setItem('user', JSON.stringify(newUserData));
      setCurrentUser(newUserData);    } catch (error) {
      handleAuthError(error, 'user update');
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!(currentUser && localStorage.getItem('token'));
  };

  // Check if user has specific role/type
  const hasRole = (role) => {
    return currentUser?.account_type === role || currentUser?.AccountType === role;
  };

  const value = {
    currentUser,
    loading,
    error,
    isInitialized,
    login,
    register,
    logout,
    updateUser,
    clearError,
    isAuthenticated,
    hasRole
  };

  // Don't render children until auth is initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 